import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Skyline } from "../components/Decor";
import { SponsorStamp } from "../components/Sponsors";
import api from "../lib/api";
import { SPONSOR_TIERS, SPONSORSHIP_PACKAGE_URL } from "../lib/data";

export default function SponsorsPage() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="sponsors-page">
      <Navbar />
      <section className="sunburst py-16 md:py-24 text-center relative">
        <div className="absolute inset-0 sunburst-green pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4">
          <p className="eyebrow text-rust mb-3">With Gratitude</p>
          <h1 className="font-display font-black text-forest text-5xl sm:text-6xl md:text-7xl tracking-tight mb-4">Our Sponsors</h1>
          <p className="tagline-italic text-forest-light text-2xl md:text-3xl">The generous partners bringing Pak Fest 2026 to life.</p>
        </div>
      </section>
      <Skyline />

      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {loading ? (
            <p className="text-center font-mono text-forest-light">Loading sponsors…</p>
          ) : sponsors.length === 0 ? (
            <div className="text-center max-w-xl mx-auto border-2 border-dashed border-forest/40 px-8 py-16">
              <p className="tagline-italic text-forest text-3xl mb-3">Sponsorships are open.</p>
              <p className="font-mono text-sm text-forest-light mb-6">Be one of the founding supporters of Winnipeg's Pak Fest — your logo could sit right here as a collector's stamp.</p>
              <Link to="/#get-involved" className="btn-rust px-8 py-4 text-sm" data-testid="sponsors-empty-cta">Become a Sponsor</Link>
            </div>
          ) : (
            SPONSOR_TIERS.map((tier) => {
              const list = sponsors.filter((s) => s.tier === tier.key);
              if (!list.length) return null;
              const isTitle = tier.key === "Title";
              return (
                <div key={tier.key} className="mb-16" data-testid={`tier-${tier.key}`}>
                  <div className="text-center mb-8">
                    {isTitle ? (
                      <span className="postmark inline-block px-6 py-2 text-sm -rotate-2 mb-2">{tier.label}</span>
                    ) : (
                      <h2 className="font-display font-bold text-forest text-3xl md:text-4xl">{tier.label}</h2>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-center gap-8">
                    {list.map((s) => <SponsorStamp key={s.id} s={s} size={isTitle ? "lg" : tier.key === "Champion" ? "md" : "sm"} />)}
                  </div>
                </div>
              );
            })
          )}

          <div className="text-center mt-8">
            <div className="dashed-divider max-w-md mx-auto mb-8" />
            <p className="font-display text-forest text-2xl mb-5">Want to see your brand here?</p>
            <a href={SPONSORSHIP_PACKAGE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border-2 border-rust text-rust px-6 py-3 text-sm font-mono hover:bg-rust hover:text-cream transition-colors mb-5" data-testid="sponsors-package-link">
              <FileText size={16} /> Download Sponsorship Package (PDF)
            </a>
            <div>
              <Link to="/#get-involved" className="btn-forest px-8 py-4 text-sm" data-testid="sponsors-cta">Apply To Sponsor</Link>
            </div>
            <div className="mt-8">
              <Link to="/" className="eyebrow text-rust inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="sponsors-back">
                <ArrowLeft size={16} /> Back Home
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
